import React, { useState } from 'react';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserMinusIcon,
  UserPlusIcon,
  EyeIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const PAGE_SIZE = 50;

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(searchTerm.trim(), 300);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const queryClient = useQueryClient();

  // Récupération des utilisateurs
  // Recherche, filtres et pagination faits par le serveur (base de données)
  const { data: usersPage, isLoading } = useQuery(
    ['super-admin-users', debouncedSearch, roleFilter, statusFilter, page],
    () => axios.get('/api/super-admin/users', {
      params: { search: debouncedSearch, role: roleFilter, status: statusFilter, page, limit: PAGE_SIZE },
    }).then(res => res.data),
    { keepPreviousData: true }
  );
  const users = usersPage?.users;
  const totalUsers = usersPage?.total || 0;

  // Récupération des pays et devises
  const { data: countries } = useQuery('countries', () => axios.get('/api/countries').then(res => res.data?.data ?? []));
  useQuery('currencies', () => axios.get('/api/currencies').then(res => res.data));

  // Mutations
  const createUserMutation = useMutation(
    (userData) => axios.post('/api/super-admin/users', userData),
    {
      onSuccess: () => {
        toast.success('Utilisateur créé avec succès');
        queryClient.invalidateQueries('super-admin-users');
        setShowCreateModal(false);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la création');
      }
    }
  );

  const updateUserMutation = useMutation(
    ({ userId, userData }) => axios.put(`/api/super-admin/users/${userId}`, userData),
    {
      onSuccess: () => {
        toast.success('Utilisateur modifié avec succès');
        queryClient.invalidateQueries('super-admin-users');
        setShowEditModal(false);
        setEditingUser(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la modification');
      }
    }
  );

  const deleteUserMutation = useMutation(
    (userId) => axios.delete(`/api/super-admin/users/${userId}`),
    {
      onSuccess: () => {
        toast.success('Utilisateur supprimé avec succès');
        queryClient.invalidateQueries('super-admin-users');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  );

  const suspendUserMutation = useMutation(
    ({ userId, suspended }) => axios.patch(`/api/super-admin/users/${userId}/suspend`, { suspended }),
    {
      onSuccess: () => {
        toast.success('Statut utilisateur modifié avec succès');
        queryClient.invalidateQueries('super-admin-users');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Erreur lors de la modification');
      }
    }
  );

  const roles = [
    { value: 'utilisateur', label: 'Utilisateur' },
    { value: 'entrepreneur', label: 'Entrepreneur' },
    { value: 'investisseur', label: 'Investisseur' },
    { value: 'admin', label: 'Admin' },
    { value: 'super-admin', label: 'Super Admin' }
  ];

  const statuses = [
    { value: 'active', label: 'Actif', color: 'green' },
    { value: 'suspended', label: 'Suspendu', color: 'red' },
    { value: 'pending', label: 'En attente', color: 'yellow' }
  ];

  const handleSelectUser = (user) => {
    setSelectedUsers(prev => 
      prev.includes(user.id) 
        ? prev.filter(id => id !== user.id)
        : [...prev, user.id]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users?.map(user => user.id) || []);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setShowEditModal(true);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setShowUserDetails(true);
  };

  const handleDeleteUser = (userId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleSuspendUser = (userId, currentStatus) => {
    const action = currentStatus === 'suspended' ? 'réactiver' : 'suspendre';
    if (window.confirm(`Êtes-vous sûr de vouloir ${action} cet utilisateur ?`)) {
      suspendUserMutation.mutate({ 
        userId, 
        suspended: currentStatus !== 'suspended' 
      });
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = statuses.find(s => s.value === status);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${statusConfig?.color}-100 text-${statusConfig?.color}-800`}>
        {statusConfig?.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const roleConfig = roles.find(r => r.value === role);
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        {roleConfig?.label}
      </span>
    );
  };

  const CreateUserModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Créer un nouvel utilisateur</h3>
          <form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.target);
            const userData = {
              firstName: formData.get('firstName'),
              lastName: formData.get('lastName'),
              email: formData.get('email'),
              password: formData.get('password'),
              role: formData.get('role'),
              country: formData.get('country'),
              phone: formData.get('phone'),
              company: formData.get('company'),
              bio: formData.get('bio')
            };
            createUserMutation.mutate(userData);
          }}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Prénom</label>
                  <input type="text" name="firstName" required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom</label>
                  <input type="text" name="lastName" required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input type="email" name="email" required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Mot de passe</label>
                <input type="password" name="password" required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Rôle</label>
                <select name="role" required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Pays</label>
                <select name="country" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                  <option value="">Sélectionner un pays</option>
                  {countries?.map(country => (
                    <option key={country.code} value={country.code}>{country.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Téléphone</label>
                <input type="tel" name="phone" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Entreprise</label>
                <input type="text" name="company" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Biographie</label>
                <textarea name="bio" rows="3" className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"></textarea>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={createUserMutation.isLoading}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                {createUserMutation.isLoading ? 'Création...' : 'Créer'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const EditUserModal = () => {
    const [showRoleConfirmation, setShowRoleConfirmation] = useState(false);
    const [pendingUserData, setPendingUserData] = useState(null);

    const handleSubmit = (e) => {
      e.preventDefault();
      const formData = new FormData(e.target);
      const userData = {
        firstName: formData.get('firstName'),
        lastName: formData.get('lastName'),
        email: formData.get('email'),
        role: formData.get('role'),
        country: formData.get('country'),
        phone: formData.get('phone'),
        company: formData.get('company'),
        bio: formData.get('bio'),
        status: formData.get('status')
      };

      // Vérifier si c'est un changement de rôle sensible
      const isRoleChange = userData.role !== editingUser.role;
      const isSensitiveChange = isRoleChange && (
        userData.role === 'super-admin' || 
        editingUser.role === 'super-admin' ||
        userData.role === 'admin'
      );

      if (isSensitiveChange) {
        setPendingUserData(userData);
        setShowRoleConfirmation(true);
      } else {
        updateUserMutation.mutate({ userId: editingUser.id, userData });
      }
    };

    const confirmRoleChange = () => {
      updateUserMutation.mutate({ userId: editingUser.id, userData: pendingUserData });
      setShowRoleConfirmation(false);
      setPendingUserData(null);
    };

    return (
      <>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Modifier l'utilisateur</h3>
              <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Prénom</label>
                  <input type="text" name="firstName" defaultValue={editingUser?.firstName} required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom</label>
                  <input type="text" name="lastName" defaultValue={editingUser?.lastName} required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input type="email" name="email" defaultValue={editingUser?.email} required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Rôle</label>
                <select name="role" defaultValue={editingUser?.role} required className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>{role.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Statut</label>
                <select name="status" defaultValue={editingUser?.status} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                  {statuses.map(status => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Pays</label>
                <select name="country" defaultValue={editingUser?.country} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2">
                  <option value="">Sélectionner un pays</option>
                  {countries?.map(country => (
                    <option key={country.code} value={country.code}>{country.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Téléphone</label>
                <input type="tel" name="phone" defaultValue={editingUser?.phone} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Entreprise</label>
                <input type="text" name="company" defaultValue={editingUser?.company} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2" />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Biographie</label>
                <textarea name="bio" rows="3" defaultValue={editingUser?.bio} className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"></textarea>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={updateUserMutation.isLoading}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                {updateUserMutation.isLoading ? 'Modification...' : 'Modifier'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    {/* Modal de confirmation pour changements de rôle sensibles */}
    {showRoleConfirmation && (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[60]">
        <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
          <div className="mt-3">
            <div className="flex items-center mb-4">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Confirmation de changement de rôle
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Vous êtes sur le point de modifier le rôle de <strong>{editingUser?.firstName} {editingUser?.lastName}</strong> 
                de <strong>{editingUser?.role}</strong> vers <strong>{pendingUserData?.role}</strong>.
              </p>
              <p className="text-sm text-red-600 mb-6">
                <ExclamationTriangleIcon className="w-4 h-4 inline mr-1 align-[-2px]" aria-hidden="true" />Cette action sera enregistrée dans les logs de sécurité et ne peut pas être annulée.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowRoleConfirmation(false);
                    setPendingUserData(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={confirmRoleChange}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Confirmer le changement
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

  const UserDetailsModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-2/3 max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Détails de l'utilisateur</h3>
            <button
              onClick={() => setShowUserDetails(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="sr-only">Fermer</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          {selectedUser && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Informations personnelles</h4>
                  <div className="mt-2 space-y-2">
                    <p><span className="font-medium">Nom:</span> {selectedUser.firstName} {selectedUser.lastName}</p>
                    <p><span className="font-medium">Email:</span> {selectedUser.email}</p>
                    <p><span className="font-medium">Téléphone:</span> {selectedUser.phone || 'Non renseigné'}</p>
                    <p><span className="font-medium">Entreprise:</span> {selectedUser.company || 'Non renseigné'}</p>
                    <p><span className="font-medium">Pays:</span> {selectedUser.countryName || 'Non renseigné'}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900">Statut et rôle</h4>
                  <div className="mt-2 space-y-2">
                    <p><span className="font-medium">Rôle:</span> {getRoleBadge(selectedUser.role)}</p>
                    <p><span className="font-medium">Statut:</span> {getStatusBadge(selectedUser.status)}</p>
                    <p><span className="font-medium">Membre depuis:</span> {new Date(selectedUser.createdAt).toLocaleDateString('fr-FR')}</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900">Statistiques</h4>
                  <div className="mt-2 space-y-2">
                    <p><span className="font-medium">Projets créés:</span> {selectedUser.projectsCount || 0}</p>
                    <p><span className="font-medium">Commentaires:</span> {selectedUser.commentsCount || 0}</p>
                    <p><span className="font-medium">Favoris:</span> {selectedUser.favoritesCount || 0}</p>
                    <p><span className="font-medium">Dernière connexion:</span> {selectedUser.lastLogin ? new Date(selectedUser.lastLogin).toLocaleString('fr-FR') : 'Jamais'}</p>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900">Biographie</h4>
                  <p className="mt-2 text-gray-600">{selectedUser.bio || 'Aucune biographie renseignée'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des utilisateurs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* En-tête */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des utilisateurs</h1>
              <p className="mt-2 text-gray-600">
                Gérez tous les utilisateurs de la plateforme
              </p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => queryClient.invalidateQueries('super-admin-users')}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-colors flex items-center"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                Actualiser
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Nouvel utilisateur
              </button>
            </div>
          </div>
        </div>

        {/* Filtres et recherche */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un utilisateur..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                className="pl-10 w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            
            <select
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">Tous les rôles</option>
              {roles.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">Tous les statuts</option>
              {statuses.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {totalUsers} utilisateur(s)
              </span>
              {selectedUsers.length > 0 && (
                <span className="text-sm text-primary">
                  {selectedUsers.length} sélectionné(s)
                </span>
              )}
            </div>
            {totalUsers > PAGE_SIZE && (
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-40"
                >
                  Précédent
                </button>
                <span className="text-sm text-gray-600">Page {page} / {Math.max(1, Math.ceil(totalUsers / PAGE_SIZE))}</span>
                <button
                  type="button"
                  disabled={page * PAGE_SIZE >= totalUsers}
                  onClick={() => setPage(p => p + 1)}
                  className="px-2 py-1 text-sm border border-gray-300 rounded disabled:opacity-40"
                >
                  Suivant
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Tableau des utilisateurs */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectedUsers.length === users?.length && users?.length > 0}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Inscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users?.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleSelectUser(user)}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center">
                            <span className="text-white font-medium">
                              {user.firstName?.[0]}{user.lastName?.[0]}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(user.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewUser(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Voir les détails"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditUser(user)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Modifier"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleSuspendUser(user.id, user.status)}
                          className={user.status === 'suspended' ? 'text-green-600 hover:text-green-900' : 'text-yellow-600 hover:text-yellow-900'}
                          title={user.status === 'suspended' ? 'Réactiver' : 'Suspendre'}
                        >
                          {user.status === 'suspended' ? <UserPlusIcon className="h-4 w-4" /> : <UserMinusIcon className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Actions en lot */}
        {selectedUsers.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">
                {selectedUsers.length} utilisateur(s) sélectionné(s)
              </span>
              <div className="flex space-x-2">
                <button className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-md text-sm hover:bg-yellow-200">
                  Suspendre
                </button>
                <button className="px-3 py-1 bg-green-100 text-green-800 rounded-md text-sm hover:bg-green-200">
                  Réactiver
                </button>
                <button className="px-3 py-1 bg-red-100 text-red-800 rounded-md text-sm hover:bg-red-200">
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modales */}
      {showCreateModal && <CreateUserModal />}
      {showEditModal && <EditUserModal />}
      {showUserDetails && <UserDetailsModal />}
    </div>
  );
};

export default UserManagement;



